import React from 'react';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MANAGEMENT_SUMMARY_HELP, MetricHelpEntry } from '@/constants/managementSummaryHelp';

interface MetricHelpTooltipProps {
  helpKey?: string;
  customHelp?: MetricHelpEntry;
  children?: React.ReactNode;
  label?: string;
  className?: string;
  iconClassName?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  showIcon?: boolean;
}

export const MetricHelpTooltip: React.FC<MetricHelpTooltipProps> = ({
  helpKey,
  customHelp,
  children,
  label,
  className = '',
  iconClassName = 'w-3 h-3 text-slate-400 group-hover:text-slate-600 transition-colors',
  side = 'top',
  align = 'start',
  showIcon = true,
}) => {
  const help = customHelp || (helpKey ? MANAGEMENT_SUMMARY_HELP[helpKey] : null);

  if (!help) {
    return <span className={className}>{children || label}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          role="note"
          aria-label={`${help.title}: ${help.description}`}
          className={`inline-flex items-center gap-1 cursor-help group outline-none focus-visible:ring-1 focus-visible:ring-emerald-600 rounded px-0.5 -mx-0.5 ${className}`}
        >
          {children || <span>{label || help.title}</span>}
          {showIcon && <Info className={`shrink-0 ${iconClassName}`} />}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={6}
        className="max-w-xs p-3 text-xs bg-slate-900 text-slate-100 border border-slate-700 shadow-xl rounded-lg space-y-1 z-50 pointer-events-none hidden md:block"
      >
        <div className="font-bold text-white text-[11px] flex items-center gap-1.5 border-b border-slate-700/80 pb-1">
          <Info className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <span>{help.title}</span>
        </div>
        <p className="text-[11px] leading-relaxed text-slate-200">{help.description}</p>
      </TooltipContent>
    </Tooltip>
  );
};
